/**
 * Helper utilities for working with Cloudflare Images URLs
 */

interface ImageVariantOptions {
  width?: number;
  height?: number;
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
  format?: 'auto' | 'avif' | 'webp';
  quality?: number;
}

/**
 * Check if a URL is a Cloudflare Images URL
 */
export function isCloudflareImageUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname === 'imagedelivery.net';
  } catch {
    return false;
  }
}

/**
 * Get an optimized image URL with flexible variants
 *
 * @param url - The original image URL
 * @param options - Transformation options
 * @returns Optimized image URL
 *
 * @example
 * // Get a 400px wide version
 * const thumbnailUrl = getOptimizedImageUrl(imageUrl, { width: 400 });
 *
 * @example
 * // Get a 800x600 cover crop
 * const cardUrl = getOptimizedImageUrl(imageUrl, {
 *   width: 800,
 *   height: 600,
 *   fit: 'cover'
 * });
 */
export function getOptimizedImageUrl(url: string, options?: ImageVariantOptions): string {
  // If not a Cloudflare Images URL or no options, return as-is
  if (!options || !isCloudflareImageUrl(url)) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);

    // URL format: /{accountHash}/{imageId}/{variant}
    if (pathParts.length >= 3) {
      const accountHash = pathParts[0];
      const imageId = pathParts[1];

      // Build variant string
      const params: string[] = [];
      if (options.width) params.push(`w=${options.width}`);
      if (options.height) params.push(`h=${options.height}`);
      if (options.fit) params.push(`fit=${options.fit}`);
      if (options.format) params.push(`format=${options.format}`);
      if (options.quality) params.push(`quality=${options.quality}`);

      const variant = params.length > 0 ? params.join(',') : 'public';

      return `https://imagedelivery.net/${accountHash}/${imageId}/${variant}`;
    }

    return url;
  } catch {
    return url;
  }
}

/**
 * Get a responsive srcSet for an image
 *
 * @param url - The original image URL
 * @param widths - Array of widths to generate
 * @returns srcSet string
 *
 * @example
 * const srcSet = getResponsiveSrcSet(imageUrl, [400, 800, 1200]);
 * // Returns: "...imagedelivery.net/.../w=400 400w, ...imagedelivery.net/.../w=800 800w, ..."
 */
export function getResponsiveSrcSet(
  url: string,
  widths: number[] = [400, 800, 1200, 1600]
): string | undefined {
  if (!isCloudflareImageUrl(url)) {
    return undefined;
  }

  return widths
    .map((width) => {
      const optimizedUrl = getOptimizedImageUrl(url, { width });
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
}

/**
 * Get common preset URLs for an image
 */
export function getImagePresets(url: string) {
  return {
    thumbnail: getOptimizedImageUrl(url, { width: 150, height: 150, fit: 'cover' }),
    small: getOptimizedImageUrl(url, { width: 400 }),
    medium: getOptimizedImageUrl(url, { width: 800 }),
    large: getOptimizedImageUrl(url, { width: 1200 }),
    card: getOptimizedImageUrl(url, { width: 600, height: 400, fit: 'cover' }),
    hero: getOptimizedImageUrl(url, { width: 1920, height: 1080, fit: 'cover' }),
    original: url,
  };
}
